import { SrSalesOrderType } from '@plato/data/starrocks/types';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { DateText } from '../../../components/common/date-time-text.tsx';
import { TabContentTableWrapper } from '../../../components/tables/tab-content-table-wrapper.tsx';
import { CurrencyOrEmpty } from '../../../components/typography/currency-or-empty.tsx';
import { EmptyText } from '../../../components/typography/empty-text.tsx';
import { TextOrEmpty } from '../../../components/typography/text-or-empty.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { RouterOutput } from '../../../server/types/infer-trpc-types.ts';
import { useAppConfig } from '../../acl/acl-provider.tsx';
import { MaskedContent } from '../../customer-mode/components/masked-content.tsx';
import { InfoBlock } from '../../detail-pages-common/components/InfoBlock.tsx';
import { useTransactionDetailLineItemsColumns } from '../../list-views/columns/use-transaction-detail-line-items-columns.tsx';
import { DataTable } from '../../list-views/components/data-table.tsx';
import { MarginPercentage } from '../../list-views/components/margin-percentage.tsx';
import { GridAddressBlock } from '../../new-quote-order-common/components/grid-address-block.tsx';
import { IsReleasedBadge } from '../../new-quote-order-common/components/is-released-badge.tsx';

import React from 'react';
import { Link } from 'react-router-dom';

type Props = {
    type: SrSalesOrderType;
    data: RouterOutput['platoUser']['grid']['getDetailQuoteOrder']['get'];
    isLoading: boolean;
};

export const QuoteOrderDetailTab: React.FC<Props> = ({ data, type, isLoading }) => {
    const { t } = useLocale();

    const { canReadGrossMargin } = useAppConfig();

    const { lineItems = [] } = data || {};

    const lineItemsColumns = useTransactionDetailLineItemsColumns({
        type: data?.header.type,
    });

    const lineItemsTable = useReactTable({
        data: lineItems,
        columns: lineItemsColumns,
        getCoreRowModel: getCoreRowModel(),
    });

    const { header } = data;

    const totalAmountNet = header.total_amount_net_including_fees ?? header.total_amount_net_line_items;

    return (
        <>
            <IsReleasedBadge releasedDate={header?.released_date} className="w-full" />

            <InfoBlock>
                <InfoBlock.Block title={t('order_info__1_title', { ns: 'transaction-details' })}>
                    <InfoBlock.Item>
                        <InfoBlock.Title>
                            {t('order_info__1_item_title__customer_id', {
                                ns: 'transaction-details',
                            })}
                        </InfoBlock.Title>
                        <InfoBlock.Body>
                            <Link
                                to={`/customers/${header.customer_id}`}
                                className=" text-blue-700 hover:underline"
                            >
                                {header.customer_id}
                            </Link>
                        </InfoBlock.Body>
                    </InfoBlock.Item>
                    <InfoBlock.Item>
                        <InfoBlock.Title>
                            {t('order_info__1_item_title__customer', {
                                ns: 'transaction-details',
                            })}
                        </InfoBlock.Title>
                        <InfoBlock.Body>
                            <TextOrEmpty>{header.customer_name}</TextOrEmpty>
                        </InfoBlock.Body>
                    </InfoBlock.Item>
                    <InfoBlock.Item>
                        <InfoBlock.Title>
                            {t('order_info__1_item_title__sales_manager', {
                                ns: 'transaction-details',
                            })}
                        </InfoBlock.Title>
                        <InfoBlock.Body>
                            {header.sales_reps && header.sales_reps.length > 0 ? (
                                header.sales_reps
                                    .map((rep) => {
                                        if (rep.id && rep.name) {
                                            return `${rep.id}: ${rep.name}`;
                                        }
                                        if (rep.id) {
                                            return rep.id;
                                        }
                                        if (rep.name) {
                                            return rep.name;
                                        }
                                        return '-';
                                    })
                                    .join(', ')
                            ) : (
                                <EmptyText />
                            )}
                        </InfoBlock.Body>
                    </InfoBlock.Item>
                    <InfoBlock.Item>
                        <InfoBlock.Title>
                            {t('order_info__1_item_title__customer_contact', {
                                ns: 'transaction-details',
                            })}
                        </InfoBlock.Title>
                        <InfoBlock.Body>
                            <TextOrEmpty>{header.contact_name}</TextOrEmpty>
                        </InfoBlock.Body>
                    </InfoBlock.Item>
                    <InfoBlock.Item>
                        <InfoBlock.Title>
                            {t('order_info__1_item_title__po_number', {
                                ns: 'transaction-details',
                            })}
                        </InfoBlock.Title>
                        <InfoBlock.Body>
                            <TextOrEmpty>{header.po_number}</TextOrEmpty>
                        </InfoBlock.Body>
                    </InfoBlock.Item>
                </InfoBlock.Block>

                <InfoBlock.Block
                    className={'grid-cols-4'}
                    title={
                        type === SrSalesOrderType.ORDER
                            ? t('order_info__2_title', { ns: 'transaction-details' })
                            : t('quote_info__2_title', { ns: 'transaction-details' })
                    }
                >
                    <InfoBlock.Item>
                        <InfoBlock.Title>
                            {t('order_info__2_item_title__created_date', {
                                ns: 'transaction-details',
                            })}
                        </InfoBlock.Title>
                        <InfoBlock.Body>
                            {header.order_created_at_date ? (
                                <DateText date={header.order_created_at_date} />
                            ) : (
                                <EmptyText />
                            )}
                        </InfoBlock.Body>
                    </InfoBlock.Item>
                    <InfoBlock.Item>
                        <InfoBlock.Title>
                            {t('order_info__2_item_title__order_type', {
                                ns: 'transaction-details',
                            })}
                        </InfoBlock.Title>
                        <InfoBlock.Body>
                            <TextOrEmpty>
                                {header.document_type === header.document_type_name
                                    ? header.document_type
                                    : `${header.document_type}: ${header.document_type_name}`}
                            </TextOrEmpty>
                        </InfoBlock.Body>
                    </InfoBlock.Item>
                    <InfoBlock.Item>
                        <InfoBlock.Title>
                            {t('order_info__2_item_title__collector', {
                                ns: 'transaction-details',
                            })}
                        </InfoBlock.Title>
                        <InfoBlock.Body>
                            {header.clerk_id
                                ? header.clerk_id === header.clerk_name
                                    ? header.clerk_id
                                    : `${header.clerk_id}: ${header.clerk_name}`
                                : '-'}
                        </InfoBlock.Body>
                    </InfoBlock.Item>

                    <InfoBlock.Item>
                        <InfoBlock.Title>
                            {t('header_info__total_line', { ns: 'transaction-details' })}
                        </InfoBlock.Title>
                        <InfoBlock.Body>{header.line_items_count}</InfoBlock.Body>
                    </InfoBlock.Item>
                </InfoBlock.Block>

                <InfoBlock.Block
                    title={t('header_info_title', { ns: 'transaction-details' })}
                    className={
                        typeof header.total_amount_net_including_fees === 'number'
                            ? 'grid-cols-4'
                            : 'grid-cols-3'
                    }
                >
                    <InfoBlock.Item>
                        <InfoBlock.Title>
                            {t('header_info__sum_line_items', { ns: 'transaction-details' })}
                        </InfoBlock.Title>
                        <InfoBlock.Body>
                            <CurrencyOrEmpty decimals={2}>
                                {header.total_amount_net_line_items}
                            </CurrencyOrEmpty>
                        </InfoBlock.Body>
                    </InfoBlock.Item>
                    {canReadGrossMargin && (
                        <>
                            <InfoBlock.Item>
                                <InfoBlock.Title>
                                    <MaskedContent>
                                        {t('header_info__gross_margin', {
                                            ns: 'transaction-details',
                                        })}
                                    </MaskedContent>
                                </InfoBlock.Title>
                                <InfoBlock.Body>
                                    <MaskedContent>
                                        <CurrencyOrEmpty decimals={2}>{header.total_margin}</CurrencyOrEmpty>
                                    </MaskedContent>
                                </InfoBlock.Body>
                            </InfoBlock.Item>
                            <InfoBlock.Item>
                                <InfoBlock.Title>
                                    <MaskedContent>
                                        {t('header_info__gross_margin_percent', {
                                            ns: 'transaction-details',
                                        })}
                                    </MaskedContent>
                                </InfoBlock.Title>
                                <InfoBlock.Body>
                                    <MaskedContent>
                                        <MarginPercentage
                                            margin={header.total_margin}
                                            revenue={totalAmountNet}
                                        />
                                    </MaskedContent>
                                </InfoBlock.Body>
                            </InfoBlock.Item>
                        </>
                    )}
                    {typeof header.total_amount_net_including_fees === 'number' && (
                        <InfoBlock.Item>
                            <InfoBlock.Title>
                                {t('header_info__sum_incl_surcharges', {
                                    ns: 'transaction-details',
                                })}
                            </InfoBlock.Title>
                            <InfoBlock.Body>
                                <CurrencyOrEmpty decimals={2}>
                                    {header.total_amount_net_including_fees}
                                </CurrencyOrEmpty>
                            </InfoBlock.Body>
                        </InfoBlock.Item>
                    )}
                </InfoBlock.Block>

                <InfoBlock.Block
                    title={t('order_info__3_title', { ns: 'transaction-details' })}
                    className="grid-cols-6"
                >
                    <InfoBlock.Item>
                        <InfoBlock.Title>
                            {t(
                                type === SrSalesOrderType.ORDER
                                    ? 'order_info__3_item_title__order_desired_delivery_date'
                                    : 'order_info__3_item_title__quote_due_date',
                                { ns: 'transaction-details' },
                            )}
                        </InfoBlock.Title>
                        <InfoBlock.Body>
                            {type === SrSalesOrderType.ORDER ? (
                                header.desired_delivery_date ? (
                                    <DateText date={header.desired_delivery_date} />
                                ) : (
                                    <EmptyText />
                                )
                            ) : header.due_date ? (
                                <DateText date={header.due_date} />
                            ) : (
                                <EmptyText />
                            )}
                        </InfoBlock.Body>
                    </InfoBlock.Item>

                    <InfoBlock.Item>
                        <InfoBlock.Title>
                            {t('order_info__3_item_title__warehouse', {
                                ns: 'transaction-details',
                            })}
                        </InfoBlock.Title>
                        <InfoBlock.Body className="flex flex-col">
                            {!!header.storage_name && !!header.storage_id ? (
                                `${header.storage_id}: ${header.storage_name}`
                            ) : (
                                <EmptyText />
                            )}
                        </InfoBlock.Body>
                    </InfoBlock.Item>
                    <InfoBlock.Item>
                        <InfoBlock.Title>
                            {t('order_info__3_item_title__shipping_method', {
                                ns: 'transaction-details',
                            })}
                        </InfoBlock.Title>
                        <InfoBlock.Body>{header.shipping_type_name}</InfoBlock.Body>
                    </InfoBlock.Item>
                    <InfoBlock.Item>
                        <InfoBlock.Title>
                            {t('order_info__3_item_title__service_provider', {
                                ns: 'transaction-details',
                            })}
                        </InfoBlock.Title>
                        <InfoBlock.Body>
                            <TextOrEmpty>{header.shipping_type_dispatcher}</TextOrEmpty>
                        </InfoBlock.Body>
                    </InfoBlock.Item>
                    <InfoBlock.Item>
                        <InfoBlock.Title>
                            {t('order_info__3_item_title__delivery_address', {
                                ns: 'transaction-details',
                            })}
                        </InfoBlock.Title>
                        <InfoBlock.Body>
                            <GridAddressBlock address={header.delivery_address} />
                        </InfoBlock.Body>
                    </InfoBlock.Item>
                    <InfoBlock.Item>
                        <InfoBlock.Title>
                            {t('order_info__3_item_title__billing_address', {
                                ns: 'transaction-details',
                            })}
                        </InfoBlock.Title>
                        <InfoBlock.Body>
                            <GridAddressBlock address={header.billing_address} />
                        </InfoBlock.Body>
                    </InfoBlock.Item>
                </InfoBlock.Block>
            </InfoBlock>
            <TabContentTableWrapper>
                <DataTable isLoading={isLoading} table={lineItemsTable} columns={lineItemsColumns} />
            </TabContentTableWrapper>
        </>
    );
};
